import { useCallback, useEffect, useState } from 'react';
import { CTA, TCTA } from '../CTA';
import {
    SpiffHostedExperience,
    SpiffHostedExperienceInstance,
    SpiffHostedExperienceResult,
    SpiffProduct,
    SpiffProductInstance,
} from './types';
import { useConfig } from '~/shared/hooks';
import { TProductDetails } from '../../ProductCard.definition';
import { ProductEventData } from '~/shared/hooks/useEvents/models';
import { useBasket } from '~/features/basket';
import { Variant } from '~/lib/data-contract/salesforce';
import { useTranslation } from '~/shared/utils/translation';
import { useFrame } from '~/shared/utils';

declare global {
    interface Window {
        Spiff?: {
            Product: SpiffProduct;
            HostedExperience: SpiffHostedExperience;
            IntegrationProduct(): void;
            setRegion(region: string): void;
            getRegion(): string;
        };
    }
}

type PersonalizationProps = Pick<TCTA, 'orderOnMobile'> & {
    product: TProductDetails;
    onComplete?(result: SpiffHostedExperienceResult): void;
    eventData: ProductEventData;
};

const Personalization: React.FC<PersonalizationProps> = ({
    product,
    eventData,
    orderOnMobile,
    onComplete,
}) => {
    const { config } = useConfig();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { addItemToBasket, toggleMiniBasket } = useBasket();

    const { id, cust_in_stock } = product;

    const [spiffProduct, setSpiffProduct] = useState<SpiffProductInstance>();
    const [hostedExperienceInstance, setHostedExperienceInstance] =
        useState<SpiffHostedExperienceInstance>();

    const addPersonalizedProductToCart = async (result: SpiffHostedExperienceResult) => {
        const payload: Variant = {
            productId: product.id,
            orderable: cust_in_stock,
            quantity: 1,
            c_description2: result.transactionId,
            c_personalization_metadata: JSON.stringify(result.designMetaData.metadata),
        };

        addItemToBasket(eventData, payload, 1)
            .then(() => {
                if (typeof onComplete === 'function') onComplete(result);
            })
            .finally(resetHostedExperience);

        toggleMiniBasket(true);
    };

    const initializeHostedExperience = () => {
        if (typeof window.Spiff === 'undefined') return;

        const hostedExperience = new window.Spiff.HostedExperience({
            applicationKey: config.SPIFF_APPLICATION_KEY,
            product: spiffProduct,
            presentmentCurrency: frame.market?.currency?.currency || 'DKK',
        });

        setHostedExperienceInstance(hostedExperience);

        hostedExperience.on('complete', addPersonalizedProductToCart);
        hostedExperience.on('quit', resetHostedExperience);

        return hostedExperience;
    };

    const resetHostedExperience = useCallback(() => {
        // Clear current hosted experience.
        setHostedExperienceInstance(undefined);

        // Removes inline style added by Spiff when opening up workflow.
        if (typeof document !== 'undefined') {
            const body = document.querySelector('body');

            // Add buffer for style changes from Spiff.
            setTimeout(() => {
                if (body) body.style.overflow = '';
            }, 100);
        }
    }, []);

    const onClick = () => {
        // Don't do anything if there's already an existing instance or if there's no Spiff product initialized.
        if (hostedExperienceInstance || !spiffProduct) return;

        const hostedExperience = initializeHostedExperience();

        // Start hosted workflow.
        hostedExperience?.execute();
    };

    // On load, we try and initialize an instance of Spiff's Product API.
    useEffect(() => {
        const initializeSpiffForProduct = async () => {
            const preferredRegion = 'us';

            if (typeof window.Spiff !== 'undefined') {
                /**
                 * Non-US users will be defaulted to use AU servers of Spiff.
                 * We will force it to use US servers since our data resides there.
                 */
                if (window.Spiff.getRegion() !== preferredRegion) {
                    window.Spiff.setRegion(preferredRegion);
                }

                // Clear set Spiff product on consequent triggers.
                setSpiffProduct(undefined);

                const product = new window.Spiff.Product({
                    productId: id,
                    integrationId: config.SPIFF_INTEGRATION_ID,
                });

                const isValid = await product.confirmActive();

                if (isValid) setSpiffProduct(product);
            }
        };

        // If Spiff is already loaded, start initialization.
        if (typeof window.Spiff !== 'undefined') {
            initializeSpiffForProduct();
        } else {
            // Otherwise, wait for it to be ready.
            window.addEventListener('SpiffApiReady', initializeSpiffForProduct);
        }

        return () => {
            window.removeEventListener('SpiffApiReady', initializeSpiffForProduct);

            // Remove set Spiff instances on cleanup.
            setSpiffProduct(undefined);
            setHostedExperienceInstance(undefined);
        };
    }, [id]);

    return (
        <CTA
            variant="pdp-secondary"
            text={translate('pdp.personalize')}
            orderOnMobile={orderOnMobile}
            disabled={!spiffProduct}
            onClick={onClick}
        />
    );
};

export default Personalization;
